import { useId } from 'react'
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import styles from './Field.module.css'

interface FieldShellProps {
  label: string
  /** Explains the field BEFORE the user gets it wrong. */
  hint?: ReactNode
  error?: string | null
  required?: boolean
  children: (id: string, describedBy: string | undefined) => ReactNode
}

/**
 * Shared wrapper: label, hint, error, and the ARIA wiring between them.
 *
 * Centralised because the wiring is easy to get subtly wrong per-field, and a
 * hint that is not linked by `aria-describedby` is invisible to a screen reader
 * even though it is right there on screen.
 */
function FieldShell({
  label,
  hint,
  error,
  required,
  children,
}: FieldShellProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>

      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}

      {children(id, describedBy)}

      {/*
        `role="alert"` so the message is announced when it appears — a
        validation error the user cannot see is not an error message.
      */}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

// ------------------------------------------------------------------- input
interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
  /** Rendered inside the input, before the value. For currency symbols. */
  prefix?: string
}

export function TextField({
  label,
  hint,
  error,
  required,
  prefix,
  className,
  onWheel,
  onKeyDown,
  ...rest
}: TextFieldProps) {
  const isNumeric = rest.type === 'number'

  /*
   * *** A NUMBER FIELD MUST ONLY CHANGE WHEN IT IS TYPED INTO ***
   * A focused `type="number"` input responds to the scroll wheel and to the
   * arrow keys. Both are silent: scrolling past a focused Price field edits
   * the price, and the admin saves a number nobody chose.
   *
   * Money and quotas are exactly the values where that is unacceptable, so
   * both gestures are blocked and the spinner buttons are hidden in CSS. The
   * value is still fully editable by typing, and `inputMode="decimal"` keeps
   * the numeric keypad on mobile.
   */
  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    // blur() rather than preventDefault(): a passive wheel listener cannot
    // cancel the event, but an unfocused input ignores the wheel entirely.
    // The page keeps scrolling, which is what the user actually wanted.
    if (isNumeric) event.currentTarget.blur()
    onWheel?.(event)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isNumeric && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault()
    }
    onKeyDown?.(event)
  }

  return (
    <FieldShell label={label} hint={hint} error={error} required={required}>
      {(id, describedBy) => (
        <div className={prefix ? styles.inputWithPrefix : undefined}>
          {prefix && <span className={styles.prefix}>{prefix}</span>}
          <input
            id={id}
            className={[
              styles.input,
              isNumeric ? styles.noSpinner : '',
              error ? styles.inputError : '',
              className ?? '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            required={required}
            // AFTER {...rest}: spreading last would overwrite these handlers
            // with the caller's, silently restoring the scroll-to-edit bug.
            // Any caller-supplied handler is still invoked, from inside ours.
            {...rest}
            inputMode={isNumeric ? 'decimal' : rest.inputMode}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}
    </FieldShell>
  )
}

// ------------------------------------------------------------------ select
interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
  options: Array<{ value: string; label: string; disabled?: boolean }>
  placeholder?: string
}

export function SelectField({
  label,
  hint,
  error,
  required,
  options,
  placeholder,
  className,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required}>
      {(id, describedBy) => (
        <select
          id={id}
          className={[
            styles.input,
            styles.select,
            error ? styles.inputError : '',
            className ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  )
}

// ---------------------------------------------------------------- textarea
interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  className,
  rows = 3,
  ...rest
}: TextAreaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required}>
      {(id, describedBy) => (
        <textarea
          id={id}
          rows={rows}
          className={[
            styles.input,
            styles.textarea,
            error ? styles.inputError : '',
            className ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          {...rest}
        />
      )}
    </FieldShell>
  )
}

// ---------------------------------------------------------------- checkbox
interface CheckboxFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string
  hint?: ReactNode
}

export function CheckboxField({
  label,
  hint,
  className,
  ...rest
}: CheckboxFieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined

  return (
    <div className={styles.checkboxField}>
      <div className={styles.checkboxRow}>
        <input
          id={id}
          type="checkbox"
          className={[styles.checkbox, className ?? ''].filter(Boolean).join(' ')}
          aria-describedby={hintId}
          {...rest}
        />
        <label htmlFor={id} className={styles.checkboxLabel}>
          {label}
        </label>
      </div>
      {hint && (
        <p id={hintId} className={styles.checkboxHint}>
          {hint}
        </p>
      )}
    </div>
  )
}

// ------------------------------------------------------------- multi-select
interface MultiSelectProps {
  label: string
  hint?: ReactNode
  error?: string | null
  required?: boolean
  options: Array<{ value: string; label: string }>
  value: string[]
  onChange: (value: string[]) => void
  emptyMessage?: string
}

/**
 * Checkbox group rather than a `<select multiple>`.
 *
 * A native multi-select requires ctrl-click to select more than one, which
 * users routinely do not know, and it silently replaces the whole selection on
 * a plain click. For mentor-to-category assignment — where dropping a category
 * by accident makes a mentor invisible to a group of students — that failure
 * mode is not acceptable.
 */
export function MultiSelectField({
  label,
  hint,
  error,
  required,
  options,
  value,
  onChange,
  emptyMessage = 'Nothing to choose from yet.',
}: MultiSelectProps) {
  const groupId = useId()

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((entry) => entry !== optionValue)
        : [...value, optionValue],
    )
  }

  return (
    <fieldset className={styles.field} aria-describedby={hint ? `${groupId}-hint` : undefined}>
      <legend className={styles.label}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </legend>

      {hint && (
        <p id={`${groupId}-hint`} className={styles.hint}>
          {hint}
        </p>
      )}

      {options.length === 0 ? (
        <p className={styles.emptyOptions}>{emptyMessage}</p>
      ) : (
        <div className={styles.checkboxGroup}>
          {options.map((option) => (
            <label key={option.value} className={styles.checkboxChip}>
              <input
                type="checkbox"
                checked={value.includes(option.value)}
                onChange={() => toggle(option.value)}
                className={styles.checkbox}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  )
}
